import { Box, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  addDays,
  adjacentSection,
  calendarDate,
  dateKey,
  today,
  weekSection,
} from "../helpers/gamesPeriod";
export function GamesRangeControls({
  mode,
  day,
  onDay,
  range,
  onRange,
  disabled,
  statistics,
  onRefresh, refreshing, lastUpdated, refreshError,
}) {
  const { t, i18n } = useTranslation();
  const week = mode === "week";
  const fmt = (d) =>
    d.toLocaleDateString(i18n.language?.startsWith("en") ? "en-GB" : "de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
  const change = (key, value) => {
    const date = calendarDate(value);
    if (!date) return;
    const next = { ...range, [key]: date, custom: true };
    if (next.start <= next.end) onRange(next);
  };
  return (
    <Box
      data-testid="games-range"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography
        component="h2"
        variant="subtitle1"
        sx={{ width: "100%", fontWeight: 700 }}
      >
        {week
          ? `${t(
              range.custom
                ? "help.navigation.customRange"
                : "help.navigation.section",
              { week: range.week, section: range.section, year: range.year }
            )} · ${fmt(range.start)} – ${fmt(range.end)}`
          : t("help.navigation.dayHeading", { date: fmt(day) })}
      </Typography>
      <Button
        disabled={disabled}
        aria-label={t(
          week
            ? "help.navigation.previousSection"
            : "help.navigation.previousDay"
        )}
        onClick={() =>
          week ? onRange(adjacentSection(range, -1)) : onDay(addDays(day, -1))
        }
      >
        ←
      </Button>
      {week ? (
        <>
          <TextField
            size="small"
            type="date"
            label={t("help.navigation.from")}
            value={dateKey(range.start)}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: dateKey(range.end) }}
            onChange={(e) => change("start", e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label={t("help.navigation.to")}
            value={dateKey(range.end)}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateKey(range.start) }}
            onChange={(e) => change("end", e.target.value)}
          />
        </>
      ) : (
        <TextField
          size="small"
          type="date"
          label={t("help.navigation.date")}
          value={dateKey(day)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => {
            const d = calendarDate(e.target.value);
            if (d) onDay(d);
          }}
        />
      )}
      <Button
        disabled={disabled}
        aria-label={t(
          week ? "help.navigation.nextSection" : "help.navigation.nextDay"
        )}
        onClick={() =>
          week ? onRange(adjacentSection(range, 1)) : onDay(addDays(day, 1))
        }
      >
        →
      </Button>
      {onRefresh && <>
        <Button variant="outlined" disabled={disabled || refreshing} onClick={onRefresh}>
          {i18n.language?.startsWith("en") ? (refreshing ? "Updating…" : "Refresh results") : (refreshing ? "Wird aktualisiert…" : "Ergebnisse aktualisieren")}
        </Button>
        <Typography variant="caption" color={refreshError ? "error" : "text.secondary"} role="status">
          {refreshError
            ? (i18n.language?.startsWith("en") ? "Refresh failed. Previous data retained; please retry." : "Aktualisierung fehlgeschlagen. Bisherige Daten bleiben sichtbar; bitte erneut versuchen.")
            : (i18n.language?.startsWith("en") ? "Automatic refresh every 30 minutes" : "Automatischer Abgleich alle 30 Minuten")}
          {lastUpdated && ` · ${i18n.language?.startsWith("en") ? "Last fetched" : "Zuletzt abgerufen"}: ${lastUpdated.toLocaleString(i18n.language?.startsWith("en") ? "en-GB" : "de-DE")}`}
        </Typography>
      </>}
      {statistics}
      <Button
        disabled={disabled}
        onClick={() => (week ? onRange(weekSection()) : onDay(today()))}
      >
        {t(week ? "help.navigation.currentSection" : "help.navigation.today")}
      </Button>
    </Box>
  );
}
